// src/pages/stock/EditSupplier.tsx
//
// Edit Supplier
//
// Page replacement for the legacy EditSupplier modal.
// This screen edits an existing supplier loaded by :supplierId.
//
// Header policy
// - Title/description/back are defined in route handles (App.tsx).
// - This page does not inject header actions.
// - This page updates the header leading slot to show:
//   - Title: "Supplier Name (Code)"
//   - Description:
//     - "● Modified" while the form is dirty
//     - "Saved" briefly after a successful save
// - Header slots are cleared on unmount to avoid leaking state to other pages.
//
// UX / behavior
// - Compact, left-aligned layout (maxWidth=900).
// - Fully MUI-based UI.
// - Save is enabled only when the form is valid, dirty, and not busy.
// - Cancel is guarded by a discard confirmation when dirty.
// - Delete lives in a dedicated Danger Zone section and requires confirmation.
// - Uses shared confirm modal + systemAlert for feedback (no window.alert/confirm).
//
// Route: /stocks/suppliers/:supplierId/edit

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import type { SupplierUpsertRequest } from '@apis/supplier';
import { editSupplier } from '@apis/supplier';

import { useSupplierDetail } from '@queries/suppliers/useSupplierDetail';

import { dropEmptyValues } from '@utils/common';
import { usePageHeader } from '@components/common/pageHeader.context';
import DangerZoneCard from '@components/common/DangerZoneCard';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

const PAGE_MAX_WIDTH = 900;
const ACTION_MIN_WIDTH = 140;
const SAVED_HINT_MS = 1200;

const normalize = (v?: string | null) => (v ?? '').trim();

/**
 * Normalize only fields that matter for dirty checks.
 * - Prevents false dirty states due to whitespace differences.
 * - Keeps comparison stable across re-renders.
 */
const toComparable = (f: SupplierUpsertRequest) => ({
  supplierName: normalize(f.supplierName),
  managerName: normalize(f.managerName),
  email: normalize(f.email),
  phoneNumber: normalize(f.phoneNumber),
  isDeleted: !!f.isDeleted,
});

/** Lightweight deep compare for small objects (keeps dependencies minimal). */
const shallowJsonEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const EditSupplier = () => {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const supplierIdNumber = React.useMemo(() => Number(supplierId), [supplierId]);

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  // --------------------
  // Data
  // --------------------
  const { data: supplierDetailForm, isLoading } = useSupplierDetail(supplierIdNumber);
  const row = supplierDetailForm?.data?.[0];

  // --------------------
  // Route param guard
  // --------------------
  const [loadingError, setLoadingError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!Number.isFinite(supplierIdNumber) || supplierIdNumber <= 0) {
      setLoadingError('Invalid supplierId.');
      return;
    }
    setLoadingError(null);
  }, [supplierIdNumber]);

  // --------------------
  // Form state
  // --------------------
  const [form, setForm] = React.useState<SupplierUpsertRequest>({
    supplierName: '',
    managerName: '',
    email: '',
    phoneNumber: '',
    isDeleted: false,
  });

  // Baseline snapshot for dirty detection (set once data is loaded).
  const baselineRef = React.useRef<ReturnType<typeof toComparable> | null>(null);

  React.useEffect(() => {
    if (!row) return;

    const next: SupplierUpsertRequest = {
      supplierName: row.supplierName,
      managerName: row.managerName,
      email: row.email ?? '',
      phoneNumber: row.phoneNumber ?? '',
      isDeleted: !!row.isDeleted,
    };

    setForm(next);
    baselineRef.current = toComparable(next);
  }, [row]);

  // --------------------
  // Busy states
  // --------------------
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const isBusy = isLoading || isSaving || isDeleting;

  // --------------------
  // Saved hint (header)
  // --------------------
  const [savedHintUntil, setSavedHintUntil] = React.useState<number | null>(null);

  const showSavedHint = React.useMemo(() => {
    return savedHintUntil !== null && Date.now() < savedHintUntil;
  }, [savedHintUntil]);

  React.useEffect(() => {
    if (!savedHintUntil) return;

    const remaining = savedHintUntil - Date.now();
    if (remaining <= 0) {
      setSavedHintUntil(null);
      return;
    }

    const t = window.setTimeout(() => setSavedHintUntil(null), remaining);
    return () => window.clearTimeout(t);
  }, [savedHintUntil]);

  // --------------------
  // Validation / dirty
  // --------------------
  const isValid = React.useMemo(() => {
    if (!normalize(form.supplierName)) return false;
    if (!normalize(form.managerName)) return false;
    return true; // email / phone are optional
  }, [form]);

  const isDirty = React.useMemo(() => {
    const base = baselineRef.current;
    if (!base) return false;
    return !shallowJsonEqual(toComparable(form), base);
  }, [form]);

  const canSave = isValid && isDirty && !isBusy && !loadingError;

  const saveDisabledReason = React.useMemo(() => {
    if (isBusy || loadingError) return '';
    if (!isValid) return 'Fill in all required fields.';
    if (!isDirty) return 'No changes to save.';
    return '';
  }, [isBusy, loadingError, isDirty, isValid]);

  // --------------------
  // Header leading slot
  // --------------------
  React.useEffect(() => {
    const name = row?.supplierName ?? form.supplierName ?? '-';
    const code = row?.supplierCode ? ` (${row.supplierCode})` : '';
    const description = isDirty ? '● Modified' : showSavedHint ? 'Saved' : undefined;

    setPageHeaderSlots({
      leading: {
        title: `${name}${code}`,
        description,
      },
    });
  }, [form.supplierName, isDirty, row?.supplierCode, row?.supplierName, setPageHeaderSlots, showSavedHint]);

  // --------------------
  // Handlers
  // --------------------
  const handleChange =
    (key: keyof SupplierUpsertRequest) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const handleSave = React.useCallback(async () => {
    if (!Number.isFinite(supplierIdNumber) || supplierIdNumber <= 0) return;
    if (isBusy) return;

    if (!isValid) {
      systemAlert.info('Please fill in all required fields correctly.');
      return;
    }
    if (!isDirty) {
      systemAlert.info('No changes to save.');
      return;
    }

    try {
      setIsSaving(true);

      const payload = dropEmptyValues(form, ['email', 'phoneNumber']);
      await editSupplier(supplierIdNumber, payload);

      // Update baseline so Save becomes disabled again after save.
      baselineRef.current = toComparable(form);

      setSavedHintUntil(Date.now() + SAVED_HINT_MS);
      systemAlert.success('Supplier updated successfully.');

      navigate('/stocks/suppliers', { replace: true });
    } catch {
      systemAlert.error('Failed to update supplier. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [form, isBusy, isDirty, isValid, navigate, supplierIdNumber, systemAlert]);

  const handleDelete = React.useCallback(async () => {
    if (!Number.isFinite(supplierIdNumber) || supplierIdNumber <= 0) return;
    if (isBusy) return;

    const ok = await confirm({
      priority: 'danger',
      title: 'Delete Supplier',
      description: (
        <>
          This action will mark the supplier as deleted. You can’t easily undo this action.
          <br />
          <b>
            {row?.supplierName} {row?.supplierCode ? `(${row.supplierCode})` : ''}
          </b>
        </>
      ),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      setIsDeleting(true);

      await editSupplier(supplierIdNumber, {
        ...form,
        isDeleted: true,
      });

      systemAlert.success('Supplier deleted.');
      navigate('/stocks/suppliers', { replace: true });
    } catch {
      systemAlert.error('Failed to delete supplier. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [confirm, form, isBusy, navigate, row?.supplierCode, row?.supplierName, supplierIdNumber, systemAlert]);

  const handleCancel = React.useCallback(async () => {
    if (isBusy) return;

    if (!isDirty) {
      navigate(-1);
      return;
    }

    const ok = await confirm({
      priority: 'info',
      title: 'Discard changes?',
      description: 'You have unsaved changes. If you leave now, those changes will be lost.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, isBusy, isDirty, navigate]);

  // --------------------
  // Render
  // --------------------
  if (loadingError) {
    return (
      <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Alert severity="error">{loadingError}</Alert>

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!isLoading && !row) {
    return (
      <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Alert severity="error">Supplier not found.</Alert>

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            {/* Supplier name is part of the route header; keep it read-only here to avoid duplicate emphasis. */}
            <TextField
              label="Manager"
              value={form.managerName}
              onChange={handleChange('managerName')}
              required
              disabled={isBusy}
              placeholder="Enter the manager name"
            />

            <TextField
              label="E-mail"
              value={form.email}
              onChange={handleChange('email')}
              disabled={isBusy}
              placeholder="Enter the e-mail address"
            />

            <TextField
              label="Phone"
              value={form.phoneNumber}
              onChange={handleChange('phoneNumber')}
              disabled={isBusy}
              placeholder="Enter the phone number"
            />

            <Divider />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.25}
              justifyContent="flex-end"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isBusy}
                startIcon={<CloseOutlinedIcon />}
                sx={{
                  width: { xs: '100%', sm: ACTION_MIN_WIDTH },
                  flex: { sm: '0 0 auto' },
                  whiteSpace: 'nowrap',
                }}
              >
                Cancel
              </Button>

              <Tooltip title={saveDisabledReason} disableHoverListener={canSave || !saveDisabledReason}>
                <Box component="span" sx={{ width: { xs: '100%', sm: ACTION_MIN_WIDTH }, flex: { sm: '0 0 auto' } }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canSave}
                    startIcon={isSaving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                    sx={{ width: '100%', whiteSpace: 'nowrap' }}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>

            <Divider />

            <DangerZoneCard
              priority="danger"
              title="Delete Supplier"
              description={
                <>
                  Deleting will mark this supplier as deleted. This action can’t be easily undone.
                  <br />
                  <b>
                    {row?.supplierName} {row?.supplierCode ? `(${row.supplierCode})` : ''}
                  </b>
                </>
              }
              actions={
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={isBusy}
                  startIcon={isDeleting ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Delete Supplier
                </Button>
              }
            />
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default EditSupplier;
